'use client';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';

import { marketDetailsService } from '@/services/api';
import type {
  CommentQueryParams,
  CommentsResponse,
  EventResponse,
  MarketActivityQueryParams,
  MarketActivityResponse,
  MarketHoldersQueryParams,
  MarketHoldersResponse,
  PricesHistoryQueryParams,
  PricesHistoryResponse,
} from '@/types';

import { PAGINATION } from '@/constants';

export const MARKET_DETAILS_QUERY_KEYS = {
  all: ['market-details'] as const,
  detail: (id: string) => [...MARKET_DETAILS_QUERY_KEYS.all, id] as const,
  comments: (params: CommentQueryParams) =>
    [...MARKET_DETAILS_QUERY_KEYS.all, 'comments', params] as const,
  holders: (params: MarketHoldersQueryParams) =>
    [...MARKET_DETAILS_QUERY_KEYS.all, 'holders', params] as const,
  activity: (params: MarketActivityQueryParams) =>
    [...MARKET_DETAILS_QUERY_KEYS.all, 'activity', params] as const,
  pricesHistory: (params: PricesHistoryQueryParams) =>
    [...MARKET_DETAILS_QUERY_KEYS.all, 'prices-history', params] as const,
};

const getCommentOrderParams = (
  sortBy: string
): { order: 'createdAt' | 'reactionCount'; ascending: boolean } => {
  if (sortBy === 'oldest') {
    return { order: 'createdAt', ascending: true };
  }
  if (sortBy === 'popular') {
    return { order: 'reactionCount', ascending: false };
  }
  return { order: 'createdAt', ascending: false };
};

export const useGetMarketDetails = (id: string) => {
  return useQuery<EventResponse>({
    queryKey: MARKET_DETAILS_QUERY_KEYS.detail(id),
    queryFn: async () => {
      return marketDetailsService.getById(id);
    },
    enabled: !!id,
    staleTime: 10 * 30 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useGrokSummary = () => {
  return useMutation({
    mutationFn: ({ prompt, locale }: { prompt: string; locale?: string }) =>
      marketDetailsService.getGrokSummary(prompt, locale),
  });
};

const PAGINATION_LOCAL = {
  DEFAULT_COMMENT_LIMIT: 5,
  DEFAULT_HOLDERS_LIMIT: 30,
  DEFAULT_ACTIVITY_LIMIT: 10,
} as const;

export const useCommentsInfinite = (
  eventId: string | number,
  sortBy: string,
  holdersOnly: boolean
) => {
  const { order, ascending } = getCommentOrderParams(sortBy);

  const params: CommentQueryParams = {
    parent_entity_id: eventId,
    parent_entity_type: 'Event',
    limit: PAGINATION_LOCAL.DEFAULT_COMMENT_LIMIT,
    order,
    ascending,
    holders_only: holdersOnly,
    get_positions: true,
    get_reports: true,
  };

  return useInfiniteQuery<CommentsResponse>({
    queryKey: MARKET_DETAILS_QUERY_KEYS.comments(params),
    queryFn: ({ pageParam = 0 }) =>
      marketDetailsService.getCommentsPaginated({
        ...params,
        offset: pageParam as number,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.meta.offset + lastPage.meta.limit;
      return lastPage.data.length === lastPage.meta.limit ? nextOffset : undefined;
    },
    enabled: !!eventId,
  });
};

export const useMarketHolders = (params: MarketHoldersQueryParams) => {
  return useQuery<MarketHoldersResponse>({
    queryKey: MARKET_DETAILS_QUERY_KEYS.holders(params),
    queryFn: () => marketDetailsService.getHolders(params),
    enabled: !!params.market,
  });
};

export const useActivityInfinite = (params: MarketActivityQueryParams) => {
  return useInfiniteQuery<MarketActivityResponse>({
    queryKey: MARKET_DETAILS_QUERY_KEYS.activity(params),
    queryFn: ({ pageParam = 0 }) =>
      marketDetailsService.getActivity({
        ...params,
        offset: pageParam as number,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.meta.offset + lastPage.meta.limit;
      return lastPage.data.length === lastPage.meta.limit ? nextOffset : undefined;
    },
    enabled: !!params.eventId || !!params.market,
  });
};

// Prices History Query with 2-minute polling
export const usePricesHistory = (params: PricesHistoryQueryParams) => {
  return useQuery({
    queryKey: MARKET_DETAILS_QUERY_KEYS.pricesHistory(params),
    queryFn: () => marketDetailsService.getPricesHistory(params),
    enabled: !!params.market,
    refetchInterval: 2 * 60 * 1000,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    select: (data) => data.data,
  });
};
